import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Grid, Stack, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, Typography, useTheme } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import dayjs from 'dayjs';
import type { CloneDetail, HealthSnapshot } from '@api/api';
import { endpoints, qk } from '../../api/endpoints';
import type { Range } from '../../api/types';
import { EmptyState, ErrorState, SectionCard } from '../../components/common';
import { fmtCompact, fmtDateTime, fmtMoney, fmtNumber, fmtPct } from '../../lib/format';
import { lastPerBucket, perBucket, sumPoints, uptimePct, type Point } from '../../lib/series';
import { HEALTH_COLOR, HEALTH_LABEL } from '../../lib/status';

const RANGES: Range[] = ['24h', '7d', '30d', '90d'];

function ChartCard({
  title,
  total,
  data,
  kind,
  unit,
  format,
  height = 220,
}: {
  title: string;
  total?: string;
  data: Point[];
  kind: 'bar' | 'line';
  unit: 'hour' | 'day' | 'raw';
  format: (v: number) => string;
  height?: number;
}) {
  const theme = useTheme();
  const series = theme.palette.mode === 'dark' ? '#3987e5' : '#2a78d6';
  const axis = { stroke: theme.palette.text.secondary, fontSize: 11 };
  const spanDays = data.length > 1 ? (data[data.length - 1].t - data[0].t) / 86_400_000 : 0;
  const tickFmt = (t: number) => dayjs(t).format(unit === 'day' || spanDays > 2 ? 'D MMM' : 'HH:mm');
  const tip = (
    <Tooltip
      cursor={kind === 'bar' ? { fill: theme.palette.action.hover } : { stroke: theme.palette.divider }}
      contentStyle={{ background: theme.palette.background.paper, border: `1px solid ${theme.palette.divider}`, borderRadius: 8, fontSize: 12 }}
      labelStyle={{ color: theme.palette.text.secondary }}
      itemStyle={{ color: theme.palette.text.primary }}
      labelFormatter={(t: number) => (unit === 'day' ? dayjs(t).format('ddd D MMM YYYY') : unit === 'hour' ? `${dayjs(t).format('D MMM, HH:00')}–${dayjs(t).add(1, 'hour').format('HH:00')}` : fmtDateTime(new Date(t).toISOString()))}
      formatter={(v: number) => [format(v), title]}
    />
  );
  const common = { data, margin: { top: 8, right: 8, bottom: 0, left: 0 } };
  // bars start at zero; gauge lines zoom to their range so changes are visible
  const yAxis = <YAxis tick={axis} tickLine={false} axisLine={false} width={48} tickFormatter={(v: number) => fmtCompact(v)} allowDecimals={false} domain={kind === 'line' ? ['auto', 'auto'] : [0, 'auto']} />;
  const xAxis = <XAxis dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']} tickFormatter={tickFmt} tick={axis} tickLine={false} axisLine={{ stroke: theme.palette.divider }} minTickGap={24} padding={kind === 'bar' ? { left: 10, right: 10 } : undefined} />;
  const grid = <CartesianGrid vertical={false} stroke={theme.palette.divider} />;

  return (
    <SectionCard
      title={title}
      action={
        total && (
          <Typography variant="body2" fontWeight={700} sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {total}
          </Typography>
        )
      }
    >
      {data.length === 0 ? (
        <EmptyState title="No data for this range" />
      ) : (
        <Box sx={{ height }} role="img" aria-label={`${title} chart`}>
          <ResponsiveContainer width="100%" height="100%">
            {kind === 'bar' ? (
              <BarChart {...common} barCategoryGap={2}>
                {grid}
                {xAxis}
                {yAxis}
                {tip}
                <Bar dataKey="v" fill={series} radius={[4, 4, 0, 0]} maxBarSize={22} isAnimationActive={false} />
              </BarChart>
            ) : (
              <LineChart {...common}>
                {grid}
                {xAxis}
                {yAxis}
                {tip}
                <Line dataKey="v" stroke={series} strokeWidth={2} dot={false} activeDot={{ r: 4, strokeWidth: 2, stroke: theme.palette.background.paper }} isAnimationActive={false} />
              </LineChart>
            )}
          </ResponsiveContainer>
        </Box>
      )}
    </SectionCard>
  );
}

/** One thin cell per health check: green/amber/red, readable at a glance. */
function HealthStrip({ snaps }: { snaps: HealthSnapshot[] }) {
  const cells = useMemo(() => {
    const max = 120;
    if (snaps.length <= max) return snaps;
    const step = snaps.length / max;
    const order = { down: 0, degraded: 1, unknown: 2, stopped: 3, provisioning: 4, ok: 5 } as const;
    return Array.from({ length: max }, (_, i) => {
      const chunk = snaps.slice(Math.floor(i * step), Math.floor((i + 1) * step));
      return chunk.reduce((w, s) => (order[s.health] < order[w.health] ? s : w), chunk[0]);
    });
  }, [snaps]);
  return (
    <Box sx={{ display: 'flex', gap: '2px', height: 28 }}>
      {cells.map((s, i) => (
        <MuiTooltip key={i} title={`${fmtDateTime(s.ts)} · ${HEALTH_LABEL[s.health]}`} disableInteractive>
          <Box sx={{ flex: 1, minWidth: 2, borderRadius: '2px', bgcolor: HEALTH_COLOR[s.health], opacity: s.health === 'ok' ? 0.75 : 1 }} />
        </MuiTooltip>
      ))}
    </Box>
  );
}

export function AnalyticsTab({ clone: c }: { clone: CloneDetail }) {
  const [range, setRange] = useState<Range>('7d');
  const stats = useQuery({ queryKey: qk.stats(c.id, range), queryFn: () => endpoints.stats(c.id, range) });
  const health = useQuery({ queryKey: qk.health(c.id, range), queryFn: () => endpoints.health(c.id, range) });
  const unit: 'hour' | 'day' = range === '24h' ? 'hour' : 'day';
  const snaps = stats.data?.snapshots ?? [];
  const hs = health.data?.snapshots ?? [];
  const currency = snaps[0]?.currency ?? c.stats?.currency ?? null;

  const rides = useMemo(() => perBucket(snaps, (s) => s.rides.today, unit), [snaps, unit]);
  const revenue = useMemo(() => perBucket(snaps, (s) => s.revenue.today, unit), [snaps, unit]);
  const scooters = useMemo(() => lastPerBucket(snaps, (s) => s.scooters.available, unit), [snaps, unit]);
  const riders = useMemo(() => lastPerBucket(snaps, (s) => s.riders.total, unit), [snaps, unit]);
  const errors = useMemo(() => hs.map((s) => ({ t: new Date(s.ts).getTime(), v: s.errors1m })), [hs]);
  const uptime = uptimePct(hs);
  const per = unit === 'hour' ? 'per hour' : 'per day';

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Business numbers every 15 min, health every minute.
        </Typography>
        <ToggleButtonGroup size="small" exclusive value={range} onChange={(_, v) => v && setRange(v)} aria-label="Range">
          {RANGES.map((r) => (
            <ToggleButton key={r} value={r} sx={{ px: 2 }}>
              {r}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      {(stats.isError || health.isError) && <ErrorState error={stats.error ?? health.error} onRetry={() => (void stats.refetch(), void health.refetch())} />}

      <SectionCard
        title="Uptime"
        action={
          <Typography variant="body2" fontWeight={700} color={uptime === null ? 'text.secondary' : uptime >= 99.5 ? 'success.main' : uptime >= 98 ? 'warning.main' : 'error.main'}>
            {uptime === null ? '—' : fmtPct(uptime, 2)}
          </Typography>
        }
      >
        {hs.length ? (
          <>
            <HealthStrip snaps={hs} />
            <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="text.secondary">
                {fmtDateTime(hs[0].ts)}
              </Typography>
              <Stack direction="row" spacing={1.5}>
                {(['ok', 'degraded', 'down'] as const).map((h) => (
                  <Stack key={h} direction="row" spacing={0.5} alignItems="center">
                    <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: HEALTH_COLOR[h] }} />
                    <Typography variant="caption" color="text.secondary">
                      {HEALTH_LABEL[h]}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                now
              </Typography>
            </Stack>
          </>
        ) : (
          <EmptyState title={health.isLoading ? 'Loading…' : 'No health checks in this range'} />
        )}
      </SectionCard>

      {!stats.isLoading && snaps.length === 0 ? (
        <SectionCard>
          <EmptyState title="No business numbers yet" text="They appear after the first platform-stats run (every 15 min). Old releases without scripts/platform-stats.js raise a “stats missing” alert." />
        </SectionCard>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ChartCard title={`Rides ${per}`} total={fmtNumber(sumPoints(rides))} data={rides} kind="bar" unit={unit} format={(v) => fmtNumber(v)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title={`Revenue ${per}`} total={fmtMoney(sumPoints(revenue), currency)} data={revenue} kind="bar" unit={unit} format={(v) => fmtMoney(v, currency)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Available scooters" data={scooters} kind="line" unit={unit} format={(v) => fmtNumber(v)} />
          </Grid>
          <Grid item xs={12} md={6}>
            <ChartCard title="Riders, total" total={riders.length ? `+${fmtNumber(riders[riders.length - 1].v - riders[0].v)}` : undefined} data={riders} kind="line" unit={unit} format={(v) => fmtNumber(v)} />
          </Grid>
        </Grid>
      )}
      <ChartCard title="Error lines per minute" total={fmtNumber(sumPoints(errors))} data={errors} kind="bar" unit="raw" format={(v) => fmtNumber(v)} height={160} />
    </Stack>
  );
}
